import { useEffect } from 'react';
import { useReactiveVar } from '@apollo/client/react';
import { Alert, Button, Card, Divider, Flex, Tag, Typography } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import dayjs from 'dayjs';
import DisplayValue from 'components/DisplayValue/DisplayValue';
import useModalControls from 'hooks/useModalControls';
import { PRECO_OFFER_STATES } from 'utilities/constants';
import { formatDollarsFromCents } from 'utilities/helpers';
import { CalendarOutlined, EditOutlined } from '@ant-design/icons';
import { getSavingsPercentage } from '../offerHelpers';
import EstimatedPaymentsBreakdown from '../EstimatedPaymentsBreakdown';
import OfferSetupGrid from '../OfferSetupGrid';
import PrecoCancelOfferModal from './PrecoCancelOfferModal';
import PrecoCancelPaymentPlanModal from './PrecoCancelPaymentPlanModal';
import PrecoChangeFundingAccountModal from './PrecoChangeFundingAccountModal';
import PrecoPaymentPlan from './PrecoPaymentPlan';
import {
  clearPrecoPlanCreationError,
  precoPlanCreationErrorVar,
} from './precoState';
import ThirdPartyAlerts from '../ThirdPartyAlerts';

const { Text } = Typography;

const OFFER_STATE_DISPLAY = {
  [PRECO_OFFER_STATES.IN_PROGRESS]: {
    label: 'In Progress',
    color: 'processing',
  },
  [PRECO_OFFER_STATES.COMPLETED]: { label: 'Completed', color: 'success' },
  [PRECO_OFFER_STATES.PENDING_COMPLETION]: {
    label: 'Pending Completion',
    color: 'warning',
  },
  [PRECO_OFFER_STATES.TERMINATED]: { label: 'Terminated', color: 'error' },
};

const PrecoOfferDetails = ({
  offer,
  thirdParty,
  onSetupPaymentPlan,
  createdAt,
}) => {
  const { enablePrecoCancellation, enablePrecoPaymentPlans } = useFlags();
  const cancelOfferModal = useModalControls();
  const cancelPaymentPlanModal = useModalControls();
  const fundingAccountModal = useModalControls();

  const remainingBalance = Math.max(0, offer.remainingBalance);
  const amountPaid = Math.max(
    0,
    offer.settlementBalance - offer.remainingBalance,
  );

  const stateConfig = OFFER_STATE_DISPLAY[offer.offerState] || {
    label: offer.offerState,
    color: 'default',
  };

  const expirationDate = dayjs(offer.expirationDate);
  const monthsRemaining = Math.max(
    1,
    Math.ceil(expirationDate.diff(dayjs(), 'month', true)),
  );
  const totalSavings =
    offer.originalOutstandingBalance - offer.settlementBalance;
  const savingsPercentage = getSavingsPercentage(
    offer.settlementBalance,
    offer.originalOutstandingBalance,
  );

  const hasPaymentPlan = enablePrecoPaymentPlans && !!offer.paymentPlanId;
  const isInProgress = offer.offerState === PRECO_OFFER_STATES.IN_PROGRESS;
  const showEstimatedPayments =
    !hasPaymentPlan && remainingBalance > 0 && isInProgress;

  const planCreationError = useReactiveVar(precoPlanCreationErrorVar);
  const isMissingPaymentPlan = enablePrecoPaymentPlans && showEstimatedPayments;
  const showPlanCreationError =
    isMissingPaymentPlan && planCreationError.offerId === offer.offerId;

  useEffect(() => {
    if (hasPaymentPlan && planCreationError.offerId === offer.offerId) {
      clearPrecoPlanCreationError();
    }
  }, [hasPaymentPlan, planCreationError.offerId, offer.offerId]);

  const hidePrompt =
    offer.offerState === PRECO_OFFER_STATES.COMPLETED ||
    offer.offerState === PRECO_OFFER_STATES.PENDING_COMPLETION;

  return (
    <>
      <ThirdPartyAlerts thirdParty={thirdParty} hidePrompt={hidePrompt} />
      {showPlanCreationError && (
        <Alert
          banner
          type="error"
          showIcon
          title={planCreationError.message}
          style={{
            width: 'fit-content',
            marginBottom: 'var(--spacing-sm)',
          }}
        />
      )}
      {isMissingPaymentPlan && (
        <Alert
          banner
          type="warning"
          showIcon
          title={
            <Flex align="center" gap="var(--spacing-sm)">
              <Text>This offer does not have a payment plan attached.</Text>
              <Button size="small" type="primary" onClick={onSetupPaymentPlan}>
                Set Up Payment Plan
              </Button>
            </Flex>
          }
          style={{
            width: 'fit-content',
            marginBottom: 'var(--spacing)',
          }}
        />
      )}
      <OfferSetupGrid>
        <Flex vertical={true} gap="var(--spacing)">
          <Card
            type="inner"
            title="Pre-Chargeoff Settlement"
            extra={<Tag color={stateConfig.color}>{stateConfig.label}</Tag>}
            styles={{ title: { whiteSpace: 'normal' } }}
          >
            <Flex vertical={true} gap="var(--spacing)">
              <Text>
                <Text strong>Deadline for settlement:</Text>{' '}
                {expirationDate.format('LL')}
              </Text>
              <Divider style={{ margin: 0 }} />
              <Flex vertical={true} gap="var(--spacing-xxs)">
                <Text>Enrollment date: {dayjs(createdAt).format('LL')}</Text>
                <Text>
                  Total Savings: {formatDollarsFromCents(totalSavings)} (
                  {savingsPercentage}%)
                </Text>
              </Flex>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  columnGap: 'var(--spacing-xl)',
                  rowGap: 'var(--spacing)',
                }}
              >
                <DisplayValue label="Settlement Amount">
                  {formatDollarsFromCents(offer.settlementBalance)}
                </DisplayValue>
                <DisplayValue label="Remaining Balance">
                  {formatDollarsFromCents(remainingBalance)}
                </DisplayValue>
                <DisplayValue label="Original Outstanding Balance">
                  {formatDollarsFromCents(offer.originalOutstandingBalance)}
                </DisplayValue>
                <DisplayValue label="Total Amount Paid">
                  {formatDollarsFromCents(amountPaid)}
                </DisplayValue>
              </div>
              {hasPaymentPlan && (
                <>
                  <Divider style={{ margin: 0 }} />
                  <Flex vertical={true} gap={4}>
                    <Flex align="center" justify="space-between">
                      <Text strong>Funding Account</Text>
                      {isInProgress && (
                        <Button
                          icon={<EditOutlined />}
                          type="text"
                          onClick={fundingAccountModal.show}
                        />
                      )}
                    </Flex>
                    <Text type="secondary">
                      {offer.paymentPlan?.fundingAccount
                        ? `${offer.paymentPlan.fundingAccount.bankName} ...${offer.paymentPlan.fundingAccount.numberLast4}`
                        : 'No Funding Account'}
                    </Text>
                  </Flex>
                </>
              )}
            </Flex>
          </Card>
          {isInProgress && enablePrecoCancellation && (
            <Flex justify="flex-start">
              <Button danger type="text" onClick={cancelOfferModal.show}>
                Cancel Offer
              </Button>
            </Flex>
          )}
          {showEstimatedPayments && (
            <Card type="inner" title="Estimated Payments" size="small">
              <EstimatedPaymentsBreakdown
                amount={remainingBalance}
                durationMonths={monthsRemaining}
                expirationDate={expirationDate}
                intro={
                  <>
                    <CalendarOutlined />{' '}
                    <strong>
                      With {formatDollarsFromCents(remainingBalance)} remaining
                      and {monthsRemaining}{' '}
                      {monthsRemaining === 1 ? 'month' : 'months'} left on the
                      offer,
                    </strong>{' '}
                    a customer would owe around:
                    <br />
                  </>
                }
              />
            </Card>
          )}
        </Flex>
        {hasPaymentPlan && (
          <Flex
            vertical={true}
            gap="var(--spacing-sm)"
            style={{ paddingLeft: 'var(--spacing-lg)' }}
          >
            <PrecoPaymentPlan
              offerId={offer.offerId}
              payments={offer.payments}
              paymentPlan={offer.paymentPlan}
              createdAt={createdAt}
              expirationDate={offer.expirationDate}
              settlementBalance={offer.settlementBalance}
              originalOutstandingBalance={offer.originalOutstandingBalance}
              titleAction={
                isInProgress && (
                  <Button
                    danger
                    type="text"
                    onClick={cancelPaymentPlanModal.show}
                  >
                    Cancel Payment Plan
                  </Button>
                )
              }
            />
          </Flex>
        )}
      </OfferSetupGrid>
      <PrecoCancelOfferModal offerId={offer.offerId} {...cancelOfferModal} />
      {enablePrecoPaymentPlans && (
        <>
          <PrecoCancelPaymentPlanModal
            offerId={offer.offerId}
            {...cancelPaymentPlanModal}
          />
          <PrecoChangeFundingAccountModal
            offer={offer}
            {...fundingAccountModal}
          />
        </>
      )}
    </>
  );
};

export default PrecoOfferDetails;
